'use client';

import { useState, useEffect, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClockCounterClockwise,
  ArrowsLeftRight,
  ArrowCounterClockwise,
  X,
  Spinner,
} from '@phosphor-icons/react';
import { Button } from '../ui/Button';
import { useConfirm } from '../ui/ConfirmDialog';
import { useToastActions } from '../ui/Toast';
import { workflowApi } from '../../services/api';
import type { WorkflowVersion } from '../../types';

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface VersionHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
  /** Called after rollback with the updated workflow from the server */
  onRestored?: (workflow: { nodes: WorkflowVersion['nodes']; edges: WorkflowVersion['edges']; name: string; description?: string }) => void;
}

export function VersionHistoryDrawer({
  isOpen,
  onClose,
  workflowId,
  workflowName,
  onRestored,
}: VersionHistoryDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToastActions();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [compareLeft, setCompareLeft] = useState<string | null>(null);
  const [compareRight, setCompareRight] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !workflowId) return;
    setLoading(true);
    workflowApi
      .listWorkflowVersions(workflowId)
      .then((res) => {
        const raw = res as { data?: { versions?: WorkflowVersion[]; total?: number } };
        const list = raw?.data?.versions;
        setVersions(Array.isArray(list) ? list : []);
      })
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [isOpen, workflowId]);

  const handleRestore = (ver: WorkflowVersion) => {
    confirm({
      title: t('workflow.restoreVersion'),
      description: t('workflow.restoreVersionConfirm'),
      type: 'danger',
      confirmText: t('workflow.restoreVersion'),
      cancelText: t('common.cancel'),
    }).then((ok) => {
      if (!ok) return;
      setRestoringId(ver.id);
      workflowApi
        .rollbackWorkflow(workflowId, ver.id)
        .then((res) => {
          toast.success(t('workflow.restoreSuccess'));
          const wf = (res as unknown as { data?: { nodes: WorkflowVersion['nodes']; edges: WorkflowVersion['edges']; name: string; description?: string } | null })?.data;
          if (wf) onRestored?.(wf);
          onClose();
        })
        .catch(() => toast.error(t('workflow.restoreFailed')))
        .finally(() => setRestoringId(null));
    });
  };

  const handleCompare = () => {
    if (!compareLeft || !compareRight) return;
    if (compareLeft === compareRight) {
      toast.error(t('workflow.compareVersions'));
      return;
    }
    onClose();
    navigate(
      `/workflows/${workflowId}/diff?left=${encodeURIComponent(compareLeft)}&right=${encodeURIComponent(compareRight)}`
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="version-history-drawer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex"
      >
        <div
          className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm"
          onClick={onClose}
          aria-hidden
        />
        <motion.aside
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                <ClockCounterClockwise className="w-5 h-5 text-violet-600" weight="fill" />
              </div>
              <div>
                <h2 className="font-semibold text-zinc-900">{t('workflow.versionHistory')}</h2>
                <p className="text-xs text-zinc-500 truncate max-w-[200px]">{workflowName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Compare section */}
            {versions.length >= 2 && (
              <div className="rounded-xl border border-zinc-200 p-4 bg-zinc-50/50">
                <p className="text-sm font-medium text-zinc-700 mb-2">{t('workflow.compareVersions')}</p>
                <p className="text-xs text-zinc-500 mb-3">{t('workflow.compareVersionsDesc')}</p>
                <div className="flex gap-2 mb-3">
                  <select
                    className="flex-1 rounded-lg border border-zinc-200 px-3 py-2 text-sm bg-white"
                    value={compareLeft ?? ''}
                    onChange={(e) => setCompareLeft(e.target.value || null)}
                  >
                    <option value="">{t('workflow.selectVersionLeft')}</option>
                    {versions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {t('workflow.versionNumber', { number: v.versionNumber })} — {formatDate(v.createdAt)}
                      </option>
                    ))}
                  </select>
                  <select
                    className="flex-1 rounded-lg border border-zinc-200 px-3 py-2 text-sm bg-white"
                    value={compareRight ?? ''}
                    onChange={(e) => setCompareRight(e.target.value || null)}
                  >
                    <option value="">{t('workflow.selectVersionRight')}</option>
                    {versions.map((v) => (
                      <option key={v.id} value={v.id}>
                        {t('workflow.versionNumber', { number: v.versionNumber })} — {formatDate(v.createdAt)}
                      </option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<ArrowsLeftRight className="w-4 h-4" />}
                  onClick={handleCompare}
                  disabled={!compareLeft || !compareRight}
                >
                  {t('workflow.compareVersions')}
                </Button>
              </div>
            )}

            <p className="text-sm font-medium text-zinc-700">{t('workflow.versions')}</p>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="w-6 h-6 text-zinc-400 animate-spin" />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-sm text-zinc-500 py-4">{t('workflow.noVersions')}</p>
            ) : (
              <ul className="space-y-3">
                {versions.map((ver) => (
                  <li
                    key={ver.id}
                    className="flex flex-col gap-2 rounded-xl border border-zinc-200 p-3 bg-white hover:border-zinc-300"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-zinc-900">
                            {t('workflow.versionNumber', { number: ver.versionNumber })}
                          </p>
                          {ver.isRollback && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-600 border border-amber-200">
                              {t('workflow.rollbackTag')}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-500">{t('workflow.versionCreatedAt', { date: formatDate(ver.createdAt) })}</p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={
                          restoringId === ver.id ? (
                            <Spinner className="w-4 h-4 animate-spin" />
                          ) : (
                            <ArrowCounterClockwise className="w-4 h-4" />
                          )
                        }
                        onClick={() => handleRestore(ver)}
                        disabled={!!restoringId}
                      >
                        {t('workflow.restoreVersion')}
                      </Button>
                    </div>
                    
                    {/* 修改说明 */}
                    {ver.changeSummary && (
                      <p className="text-xs text-zinc-600 bg-zinc-50 rounded-lg px-2 py-1.5 border border-zinc-100">
                        <span className="text-zinc-400">{t('workflow.changeSummary')}:</span> {ver.changeSummary}
                      </p>
                    )}
                    
                    {/* 创建人信息 */}
                    {ver.createdByName && (
                      <p className="text-xs text-zinc-400">
                        {t('workflow.createdBy')}: {ver.createdByName}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </motion.aside>
      </motion.div>
      <Fragment key="version-history-confirm">{confirmDialog}</Fragment>
    </AnimatePresence>
  );
}
